import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, Input } from '@zt/ui';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import type { CompanyPage } from '../lib/types';

export interface PickedCompany {
  id: string;
  legalName: string;
  externalCode: string | null;
}

/**
 * Bayi firması seçici (F7-10): onaylı bayileri unvan, bayi kodu veya vergi numarasıyla arar; seçilenler adlarıyla
 * listelenir. Bayiye özel kampanya ve kupon kitlesinde kullanılır.
 */
export function CompanyMultiPicker({
  value,
  onChange,
  label,
  help,
  error,
  max = 100,
}: {
  value: PickedCompany[];
  onChange: (v: PickedCompany[]) => void;
  label: string;
  help?: ReactNode;
  error?: string;
  max?: number;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const results = useQuery({
    queryKey: ['company-picker-search', q],
    queryFn: () =>
      api.get<CompanyPage>(
        `/v1/admin/companies?${new URLSearchParams({ q, status: 'approved', limit: '10' })}`,
      ),
    enabled: q.trim().length >= 2,
  });
  const chosen = new Set(value.map((c) => c.id));
  const name = (c: PickedCompany) => (c.externalCode ? `${c.legalName} (${c.externalCode})` : c.legalName);
  return (
    <div className="grid gap-2">
      <Field label={label} help={help} error={error}>
        {(p) => (
          <Input
            {...p}
            type="search"
            value={q}
            placeholder={t('admin.dealerCampaigns.search')}
            onChange={(e) => setQ(e.target.value)}
          />
        )}
      </Field>
      {results.data && results.data.items.length > 0 && (
        <ul className="max-h-48 overflow-auto rounded-sm border border-line text-sm">
          {results.data.items.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                disabled={chosen.has(r.id) || value.length >= max}
                className="w-full px-3 py-1.5 text-start hover:bg-surface-2 disabled:text-ink-3"
                onClick={() => {
                  onChange([...value, { id: r.id, legalName: r.legalName, externalCode: r.externalCode }]);
                  setQ('');
                }}
              >
                {name({ id: r.id, legalName: r.legalName, externalCode: r.externalCode })}
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && (
        <ol className="grid gap-1 text-sm" aria-label={label}>
          {value.map((c) => (
            <li key={c.id} className="flex items-center gap-2 rounded-sm border border-line px-2 py-1">
              <span className="flex-1">{name(c)}</span>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`${t('common.remove')}: ${c.legalName}`}
                onClick={() => onChange(value.filter((x) => x.id !== c.id))}
              >
                {t('common.remove')}
              </Button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
